import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  BadgeDollarSign,
  Banknote,
  CalendarRange,
  Clock,
  CreditCard,
  FilePenLine,
  FileText,
  Gauge,
  Handshake,
  Hourglass,
  Landmark,
  LifeBuoy,
  Percent,
  PiggyBank,
  Receipt,
  Scale,
  Send,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

/**
 * One glyph per financial concept.
 *
 * The KPI strips were built by six agents, so the same idea arrived wearing
 * different clothes: hours were a `Timer` on /team and a `Clock` on /support,
 * revenue was `TrendingUp` on the dashboard and `BadgeDollarSign` on /team.
 * A decorative icon that means something different two screens apart is worse
 * than no icon at all, because the reader learns it and is then wrong.
 *
 * Adding a KPI? Pick a kind from this list. If nothing fits, add a kind here —
 * never a one-off `<SomeIcon />` at the call site.
 *
 * ⚠ **AND THE MAP BELOW MUST STAY INJECTIVE — one glyph, one concept.** The
 * warning above was written here and then broken here, four times over: the
 * paper plane labelled "$38,791 UNBILLED" on /invoices, /clients and a client
 * hero AND "OVERAGE 17 h" on /support; the piggy bank was margin *and* profit;
 * the target was budget *and* pipeline; and the receipt was expenses *and*
 * invoice — money out and money in wearing one glyph, which is the worst of
 * the four. A reader learns a glyph from the screen they use most and is then
 * wrong on the next one. If two kinds want the same icon, one of them is
 * either the wrong kind or needs its own glyph.
 */
export type StatKind =
  | 'hours'
  | 'utilization'
  | 'revenue'
  | 'billable'
  | 'cost'
  | 'expenses'
  | 'margin'
  | 'profit'
  | 'cash'
  | 'ar'
  | 'overdue'
  | 'draft'
  | 'unbilled'
  /** Hours worked past the end of a prepaid bank — neither free nor billed. */
  | 'overage'
  | 'invoice'
  | 'budget'
  | 'support'
  | 'people'
  | 'period'
  | 'atRisk'
  | 'pipeline'
  /** A difference between two figures (budget vs actual, plan vs forecast). */
  | 'variance';

export const STAT_ICON: Record<StatKind, LucideIcon> = {
  hours: Clock,
  utilization: Gauge,
  revenue: TrendingUp,
  billable: BadgeDollarSign,
  cost: Banknote,
  /** Money OUT of the business. The invoice is money IN — see `invoice`. */
  expenses: Receipt,
  /** A RATE — what is left of revenue after cost. Profit is the figure. */
  margin: Percent,
  profit: PiggyBank,
  cash: Landmark,
  ar: Wallet,
  overdue: CreditCard,
  /** A document still being written — the pen is the difference. */
  draft: FilePenLine,
  /** Work that could be invoiced and has not been: ready to send. */
  unbilled: Send,
  overage: Hourglass,
  /** An issued document. Money IN, so never the expenses receipt. */
  invoice: FileText,
  budget: Target,
  support: LifeBuoy,
  people: Users,
  period: CalendarRange,
  atRisk: AlertTriangle,
  /** Deals, not targets: pipeline money arrives when somebody shakes on it. */
  pipeline: Handshake,
  variance: Scale,
};

/** `<StatIcon kind="ar" />` — sized and coloured by StatCard's icon slot. */
export function StatIcon({ kind }: { kind: StatKind }) {
  const Icon = STAT_ICON[kind];
  return <Icon aria-hidden />;
}
